import React, { useState } from 'react';
import { CreditCard, Plus, Trash2, Edit3 } from 'lucide-react';
import { useAccounts, useCreateAccount, useUpdateAccount, useDeleteAccount } from '@/hooks/useApi';
import { Card, Badge, LoadingSpinner, Modal, EmptyState } from '@/components/UI';
import { formatCurrency, formatPercent, pnlColor } from '@/utils';
import type { StrategyAccount } from '@/types';

export function AccountsPage() {
  const { data: accounts, isLoading } = useAccounts();
  const createAccount = useCreateAccount();
  const updateAccount = useUpdateAccount();
  const deleteAccount = useDeleteAccount();

  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: '',
    description: '',
    initial_balance: '',
    risk_percentage: '',
    mode: 'paper',
  });

  const accountList = accounts ?? [];

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createAccount.mutateAsync({
        name: form.name,
        description: form.description,
        initial_balance: parseFloat(form.initial_balance),
        risk_percentage: parseFloat(form.risk_percentage),
        mode: form.mode,
      });
      setShowCreate(false);
      setForm({ name: '', description: '', initial_balance: '', risk_percentage: '', mode: 'paper' });
    } catch { /* handled by mutation */ }
  };

  const handleToggleActive = async (account: StrategyAccount) => {
    await updateAccount.mutateAsync({
      id: account.id,
      data: { is_active: !account.is_active },
    });
  };

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this account?')) {
      await deleteAccount.mutateAsync(id);
    }
  };

  if (isLoading) return <LoadingSpinner text="Loading accounts..." />;

  return (
    <div className="space-y-6 animate-in">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-surface-100">Strategy Accounts</h1>
        <button onClick={() => setShowCreate(true)} className="btn-primary btn-sm">
          <Plus className="w-4 h-4" /> New Account
        </button>
      </div>

      {accountList.length === 0 ? (
        <EmptyState
          icon={<CreditCard className="w-10 h-10" />}
          title="No accounts yet"
          description="Create your first strategy account to start trading"
          action={
            <button onClick={() => setShowCreate(true)} className="btn-primary btn-sm">
              Create Account
            </button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {accountList.map((account: StrategyAccount) => {
            const pnl = account.current_balance - account.initial_balance;
            const pnlPct = account.initial_balance > 0 ? (pnl / account.initial_balance) * 100 : 0;

            return (
              <Card key={account.id}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-base font-semibold text-surface-100">{account.name}</h3>
                    <p className="text-xs text-surface-500 mt-0.5">{account.description}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Badge variant={account.mode === 'paper' ? 'neutral' : 'warning'}>
                      {account.mode.toUpperCase()}
                    </Badge>
                    <Badge variant={account.is_active ? 'profit' : 'loss'}>
                      {account.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div>
                    <p className="text-xs text-surface-500">Balance</p>
                    <p className="text-lg font-bold text-surface-100">{formatCurrency(account.current_balance)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-surface-500">P&L</p>
                    <p className={`text-lg font-bold ${pnlColor(pnl)}`}>
                      {pnl >= 0 ? '+' : ''}{formatCurrency(pnl)}
                    </p>
                    <p className={`text-xs ${pnlColor(pnl)}`}>{formatPercent(pnlPct)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-surface-500">Initial</p>
                    <p className="text-sm text-surface-300">{formatCurrency(account.initial_balance)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-surface-500">Risk</p>
                    <p className="text-sm text-surface-300">{account.risk_percentage}%</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleToggleActive(account)}
                    className={account.is_active ? 'btn-ghost btn-sm' : 'btn-success btn-sm'}
                  >
                    {account.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                  <button
                    onClick={() => handleDelete(account.id)}
                    className="btn-ghost btn-sm text-loss hover:text-loss"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Account Modal */}
      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title="Create Strategy Account">
        <form onSubmit={handleCreate} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-surface-400 mb-1.5">Account Name</label>
            <input className="input" placeholder="My Strategy" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </div>
          <div>
            <label className="block text-xs font-medium text-surface-400 mb-1.5">Description</label>
            <input className="input" placeholder="Strategy description" value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })} required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-surface-400 mb-1.5">Initial Balance ($)</label>
              <input type="number" step="any" className="input" placeholder="100.00" value={form.initial_balance}
                onChange={(e) => setForm({ ...form, initial_balance: e.target.value })} required />
            </div>
            <div>
              <label className="block text-xs font-medium text-surface-400 mb-1.5">Risk Percentage (%)</label>
              <input type="number" step="0.1" className="input" placeholder="1.0" value={form.risk_percentage}
                onChange={(e) => setForm({ ...form, risk_percentage: e.target.value })} required />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-surface-400 mb-1.5">Mode</label>
            <select className="select" value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })}>
              <option value="paper">Paper Trading</option>
              <option value="live">Live Trading</option>
            </select>
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => setShowCreate(false)} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary" disabled={createAccount.isPending}>
              {createAccount.isPending ? 'Creating...' : 'Create Account'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
